// Lib
import * as Rematrix from 'rematrix';
import { Matrix3D } from 'rematrix';

// Utils
import { asObject } from '../../../common/utils/immutableHelper';
import { getElement as getElementFromMap } from '../../../common/elements/utils/elementTraversalUtils';
import { getCanvasElements as getCanvasElementsFromGraph } from '../../../common/elements/utils/elementGraphUtils';
import { getElementId, getElementType } from '../../../common/elements/utils/elementPropertyUtils';
import * as matrixLib from '../../../common/maths/matrix/matrixUtils';
import { convertClientRectToUnscaledSectionCoordinates } from '../../canvas/utils/canvasPositionUtils';

// Selectors
import { getElements } from '../../element/selectors/elementsSelector';
import { elementGraphSelector } from '../../element/selectors/elementGraphSelector';
import {
    getCurrentBoardIdFromState,
    getCurrentVisibleBoardId,
} from '../../reducers/currentBoardId/currentBoardIdSelector';
import { getSelectedElementIds as getSelectedElementIdsFromState } from '../../element/selection/selectedElementsSelector';
import {
    getCurrentlyEditingEditorId,
    getCurrentlyEditingEditorKey,
    getCurrentlyEditingId,
} from '../../element/selection/currentlyEditingSelector';
import { canvasZoomScaleSelector, canvasZoomTranslationPxPojoSelector } from '../../canvas/store/canvasZoomSelector';
import { getGridSize as getGridSizeSelector } from '../../utils/grid/gridSizeSelector';
import { getThemeSelector } from '../../theme/themeSelector';

// Constants
import { CANVAS_VIEWPORT_ID } from '../../canvas/utils/canvasConstants';

// Types
import { Point } from '../../../common/maths/geometry/pointTypes';
import { Rect } from '../../../common/maths/geometry/rect/rectTypes';
import { MNElement } from '../../../common/elements/elementModelTypes';
import { ReduxState } from '../../types/reduxTypes';

type EditingState = {
    elementId: string | null;
    editorId: string | null;
    editorKey: string | null;
};

type ElementRect = {
    x: number;
    y: number;
    width: number;
    height: number;
};

export type BoardElementInfo = {
    id: string;
    type: string;
    rect: ElementRect | null;
    selected: boolean;
};

export type VisualVerificationApi = {
    getCurrentBoardId: () => string | null;
    getVisibleBoardId: () => string | null;
    getSelectedElementIds: () => string[];
    getEditingState: () => EditingState;
    getElement: (id: string) => MNElement | null;
    getElementRect: (id: string) => ElementRect | null;
    /**
     * Returns the current board's own canvas elements (id, type, DOM rect, selection state) as
     * plain objects, so specs can assert board contents semantically instead of by screenshot.
     */
    getCanvasElements: () => BoardElementInfo[];
    /** Null when the canvas viewport is not mounted, i.e. when no conversion is possible. */
    worldToScreen: (point: Point) => Point | null;
    /** Null when the canvas viewport is not mounted, i.e. when no conversion is possible. */
    screenToWorld: (point: Point) => Point | null;
    getGridSize: () => number;
    getTheme: () => string | null;
    /**
     * Plain-object media player state for a player id (a FILE element id, or its `-modal`/`-focus`
     * variant -- see FilePreview.jsx's getPlayerId), or null before that player's first action.
     */
    getMediaPlayerState: (playerId: string) => Record<string, unknown> | null;
};

declare global {
    interface Window {
        __visualVerification?: VisualVerificationApi;
    }
}

const getCanvasViewportNode = (): HTMLElement | null => document.getElementById(CANVAS_VIEWPORT_ID);

/** Looks up an element's own DOM node by id and returns its client rect, or null when not mounted. */
const queryElementRect = (id: string): ElementRect | null => {
    // The `.Element` qualifier is load-bearing: CanvasElement.jsx renders an outer positioned
    // wrapper carrying the same data-element-id, so an unqualified selector would measure that
    // wrapper instead of the element body every caller means.
    const elementNode = document.querySelector(`.Element[data-element-id="${id}"]`);
    if (!elementNode) return null;

    const { x, y, width, height } = elementNode.getBoundingClientRect();
    return { x, y, width, height };
};

const toPointRect = ({ x, y }: Point): Rect => ({ x, y, left: x, top: y, right: x, bottom: y, width: 0, height: 0 });

/**
 * Converts a canvas-document ("world") point into a viewport ("screen") point, in CSS px, i.e.
 * the coordinate space of `Element.getBoundingClientRect()`. Null when the canvas viewport is not
 * mounted -- returning the input unchanged would hand back a world point dressed as a screen
 * point, which reads as a successful conversion and clicks the wrong place.
 *
 * Builds the same scale+translate matrix Canvas.jsx applies to CSS-transform the canvas document
 * (see `getCanvasDocumentStyle` in `Canvas.jsx`), then accounts for the canvas viewport's own
 * position and scroll offset. State fields used: `app.canvas.zoom.scale` and
 * `app.canvas.zoom.translation` (via `canvasZoomScaleSelector` / `canvasZoomTranslationPxPojoSelector`).
 */
const worldToScreen = (state: ReduxState, worldPoint: Point): Point | null => {
    const canvasViewportNode = getCanvasViewportNode();
    if (!canvasViewportNode) return null;

    const zoomScale = canvasZoomScaleSelector(state);
    // canvasZoomTranslationPxPojoSelector is defined in a plain JS file, so its `asObject`-derived
    // return type doesn't narrow past `unknown` here -- it's a plain { x, y } point at runtime.
    const zoomTranslationPx = canvasZoomTranslationPxPojoSelector(state) as Point;

    let documentMatrix: Matrix3D = Rematrix.identity();
    documentMatrix = matrixLib.setScale(documentMatrix, zoomScale);
    documentMatrix = matrixLib.setTranslation(documentMatrix, zoomTranslationPx);

    const [scaledX, scaledY] = matrixLib.multiplyMatrixAndPoint(documentMatrix, [worldPoint.x, worldPoint.y, 1, 1]);

    const viewportRect = canvasViewportNode.getBoundingClientRect();

    return {
        x: scaledX + viewportRect.left - canvasViewportNode.scrollLeft,
        y: scaledY + viewportRect.top - canvasViewportNode.scrollTop,
    };
};

/**
 * Converts a viewport ("screen") point, in CSS px, into a canvas-document ("world") point. Null
 * when the canvas viewport is not mounted -- see worldToScreen for why this is not a passthrough.
 *
 * Delegates to `convertClientRectToUnscaledSectionCoordinates`, the same function the canvas
 * uses (e.g. in `canvasUtils.ts`) to translate DOM client rects into unscaled canvas-document
 * coordinates. State fields used: `app.canvas.zoom.scale` and `app.canvas.zoom.translation`.
 */
const screenToWorld = (state: ReduxState, screenPoint: Point): Point | null => {
    const canvasViewportNode = getCanvasViewportNode();
    if (!canvasViewportNode) return null;

    const viewportRect = canvasViewportNode.getBoundingClientRect();

    const worldRect = convertClientRectToUnscaledSectionCoordinates(
        toPointRect(screenPoint),
        { x: viewportRect.left, y: viewportRect.top },
        { x: canvasViewportNode.scrollLeft, y: canvasViewportNode.scrollTop },
        canvasZoomScaleSelector(state),
        canvasZoomTranslationPxPojoSelector(state) as Point,
    );

    return { x: worldRect.x, y: worldRect.y };
};

/**
 * Returns the current board's own direct-child CANVAS-section elements as plain objects (id, type,
 * DOM rect, selection state), so specs can assert board contents semantically -- e.g. "a CARD with
 * this id exists on the board" -- instead of by screenshot. Inbox, attached and trashed children
 * are excluded, as are nested descendants (e.g. cards inside a column).
 */
const getCanvasElements = (state: ReduxState): BoardElementInfo[] => {
    const boardId = getCurrentVisibleBoardId(state) ?? getCurrentBoardIdFromState(state);
    if (!boardId) return [];

    const selectedIds = asObject<string[]>(getSelectedElementIdsFromState(state)) ?? [];

    return getCanvasElementsFromGraph({
        elements: getElements(state),
        elementGraph: elementGraphSelector(state),
        parentId: boardId,
    })
        .valueSeq()
        .toArray()
        .map((element: MNElement) => {
            const id = getElementId(element);
            return {
                id,
                type: getElementType(element),
                rect: queryElementRect(id),
                selected: selectedIds.includes(id),
            };
        });
};

/**
 * Installs `window.__visualVerification`, a facade browser-automation specs call via
 * `page.evaluate()` to assert app state semantically instead of via screenshots. Every method
 * returns plain JSON-serialisable values only -- Immutable.js state is converted at the boundary.
 */
export const installVisualVerificationApi = (getState: () => ReduxState): void => {
    if (typeof window === 'undefined') return;

    window.__visualVerification = {
        getCurrentBoardId: () => getCurrentBoardIdFromState(getState()) ?? null,

        getVisibleBoardId: () => getCurrentVisibleBoardId(getState()) ?? null,

        getSelectedElementIds: () => asObject(getSelectedElementIdsFromState(getState())) ?? [],

        getEditingState: () => {
            const state = getState();
            return {
                elementId: getCurrentlyEditingId(state) ?? null,
                editorId: getCurrentlyEditingEditorId(state) ?? null,
                editorKey: getCurrentlyEditingEditorKey(state) ?? null,
            };
        },

        getElement: (id) => {
            const element = getElementFromMap(getElements(getState()), id);
            return element ? asObject<MNElement>(element) : null;
        },

        getElementRect: (id) => queryElementRect(id),

        getCanvasElements: () => getCanvasElements(getState()),

        worldToScreen: (point) => worldToScreen(getState(), point),
        screenToWorld: (point) => screenToWorld(getState(), point),

        getGridSize: () => getGridSizeSelector(getState()),

        getTheme: () => getThemeSelector(getState()) ?? null,

        getMediaPlayerState: (playerId) => {
            const playerState = getState().getIn(['app', 'mediaPlayer', playerId]);
            return playerState ? asObject<Record<string, unknown>>(playerState) : null;
        },
    };
};
